import React from 'react';
import { useFormikContext } from 'formik';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import { AcceptRequestFlowValues } from '../../../types/appointments';
import {
  schedulingTypeOptions,
  sessionCountOptions,
} from '../../../utils/form/acceptRequestFlow';
import { getDepositPlacementSelectInput } from '../../../helpers/appointments';
import { Input, InputRowType } from '../../../interfaces';
import useConfirm from '../../../hooks/useConfirm';
import InputGroup from '../InputGroup';
import CollapsibleSection from './CollapsibleSection';

const SECTION_FIELDS = [
  'numberOfSessions',
  'schedulingType',
  'depositPlacement',
  'bufferDays',
] as const;

interface Props {
  clientFirstName: string;
}

function MultiSessionOptions({ clientFirstName }: Props) {
  const { values, setFieldValue, errors, submitCount } =
    useFormikContext<AcceptRequestFlowValues>();
  const confirm = useConfirm();

  const isConsultation = values.requestKind === 'CONSULTATION';
  const isBookingLink = values.schedulingMode === 'BOOKING_LINK';
  const isMultiSession = values.numberOfSessions > 1;

  // Going multi-session hides the offer-times option, so the mode and the
  // picked times are about to be dropped — say so before it happens
  const confirmDroppingOfferedTimes = React.useCallback(
    async (value: string) => {
      if (Number(value) <= 1 || values.schedulingMode !== 'OFFER_TIMES')
        return true;
      return confirm({
        dismissible: true,
        title: 'Switch to a booking link?',
        message: values.offeredSlots?.length
          ? `Multi-session bookings can't be offered set times. ${clientFirstName} will get a booking link to pick a time instead, and the times you picked will be cleared.`
          : `Multi-session bookings can't be offered set times. ${clientFirstName} will get a booking link to pick a time instead.`,
        positiveText: 'Switch',
        negativeText: 'Cancel',
      });
    },
    [clientFirstName, confirm, values.offeredSlots, values.schedulingMode],
  );

  const inputRows = React.useMemo(
    () =>
      [
        [
          {
            name: 'numberOfSessions',
            label: 'Number of Sessions *',
            type: 'select',
            placeholder: 'Enter number of sessions',
            options: sessionCountOptions,
            validateChange: confirmDroppingOfferedTimes,
          },
        ],
        isMultiSession &&
          isBookingLink && [
            {
              name: 'schedulingType',
              label: 'Scheduling Type',
              type: 'select',
              options: schedulingTypeOptions,
              placeholder:
                'How do you want clients to schedule these appointments?',
              onChange: (value: string) => {
                if (value === 'FIRST_SESSION') {
                  setFieldValue('depositPlacement', 'FIRST');
                }
              },
            },
          ],
        isMultiSession &&
          (values.depositPrice ?? 0) > 0 && [
            getDepositPlacementSelectInput({
              disabled:
                isBookingLink && values.schedulingType === 'FIRST_SESSION',
            }),
          ],
        isMultiSession &&
          isBookingLink && [
            {
              name: 'bufferDays',
              label: 'Buffer Days',
              type: 'number',
              min: 0,
              step: 1,
              placeholder:
                'How many days should be in-between the customers appointments?',
            } as Input,
          ],
      ].filter(Boolean) as InputRowType[],
    [
      confirmDroppingOfferedTimes,
      isBookingLink,
      isMultiSession,
      setFieldValue,
      values.depositPrice,
      values.schedulingType,
    ],
  );

  if (isConsultation) return null;

  const hasSectionError = SECTION_FIELDS.some(field => !!errors[field]);

  return (
    <CollapsibleSection
      title="Multi-Session"
      subtitle="Break the work into multiple appointments"
      icon={<RepeatRoundedIcon sx={{ color: 'secondary.main' }} />}
      chips={isMultiSession ? [`${values.numberOfSessions} sessions`] : []}
      errorSignal={hasSectionError ? submitCount : 0}>
      <InputGroup name="" rows={inputRows} />
    </CollapsibleSection>
  );
}

export default MultiSessionOptions;
